import { MirrorInputAdapter } from "./mirrorInputAdapter";
import type { BoardState, Position } from "../game/types";
import { canSwap, swapCells } from "../game/board";
import { cloneBoard } from "../game/grid";
import { createsMatchAfterSwap } from "../game/match";
import { activationFromSwap } from "../game/specials";
import type { BotSkillProfile } from "./botSkillProfile";
import type { TrapKind } from "./types";
import { TRAP_CONFIGS } from "./versusConstants";

export type RandomSource = () => number;

export type BotMoveBucket = "strong" | "neutral" | "mistake";

export interface BotSwapCandidate {
  from: Position;
  to: Position;
}

export interface BotMoveWeights {
  strong: number;
  neutral: number;
  mistake: number;
}

export interface BotDecisionRuntime {
  botLeadScoreDiff: number;
  antiSnowballEase: number;
}

export interface BotDecisionWeightsSnapshot {
  strong: number;
  neutral: number;
  mistake: number;
}

export interface BotDecisionDiagnostics {
  selectedBucket: BotMoveBucket | "fallback_any";
  antiSnowballTarget: number;
  antiSnowballEase: number;
  leadScoreDiff: number;
  leadStartScoreDiff: number;
  bucketsAvailable: {
    strong: number;
    neutral: number;
    mistake: number;
  };
  baseWeights: BotDecisionWeightsSnapshot;
  effectiveWeights: BotDecisionWeightsSnapshot;
}

export interface BotMoveBuckets {
  strong: BotSwapCandidate[];
  neutral: BotSwapCandidate[];
  mistake: BotSwapCandidate[];
}

export interface BotTrapDecisionContext {
  localBoard: BoardState | null;
  localFogActive: boolean;
  localCash: number;
  localDoomSec: number;
  localGroovePct: number;
  localGrooveActive: boolean;
  localGrooveArmed: boolean;
  remoteCash: number;
}

const STRONG_WEIGHT_CEILING: Record<BotSkillProfile["segment"], number> = {
  novice: 0.52,
  midcore: 0.62,
  experienced: 0.68
};

const BOT_TRAP_DELAY_RANGES_SEC: Record<BotSkillProfile["segment"], { min: number; max: number }> = {
  novice: { min: 6, max: 9 },
  midcore: { min: 4.5, max: 7 },
  experienced: { min: 3.5, max: 5.5 }
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeWeights(weights: BotMoveWeights): BotMoveWeights {
  const strong = Math.max(0, weights.strong);
  const neutral = Math.max(0, weights.neutral);
  const mistake = Math.max(0, weights.mistake);
  const total = strong + neutral + mistake;
  if (total <= 0) {
    return { strong: 0.34, neutral: 0.38, mistake: 0.28 };
  }
  return {
    strong: strong / total,
    neutral: neutral / total,
    mistake: mistake / total
  };
}

function applyFairnessEnvelope(
  skillProfile: BotSkillProfile,
  weights: BotMoveWeights
): BotMoveWeights {
  let result = normalizeWeights(weights);
  const strongCeiling = STRONG_WEIGHT_CEILING[skillProfile.segment];
  const neutralFloor = skillProfile.segment === "novice" ? 0.22 : 0.18;
  const mistakeFloor = clamp(skillProfile.fairness.mistakeAllowance * 0.45, 0.06, 0.18);

  result.strong = Math.min(result.strong, strongCeiling);
  result.neutral = Math.max(result.neutral, neutralFloor);
  result.mistake = Math.max(result.mistake, mistakeFloor);
  result = normalizeWeights(result);

  if (result.strong > strongCeiling) {
    const excess = result.strong - strongCeiling;
    result.strong = strongCeiling;
    result.neutral += excess * 0.7;
    result.mistake += excess * 0.3;
    result = normalizeWeights(result);
  }

  return result;
}

function bucketWeightsForAvailableMoves(
  baseWeights: BotMoveWeights,
  buckets: BotMoveBuckets
): BotMoveWeights {
  const weighted = { ...baseWeights };
  if (buckets.strong.length === 0) {
    weighted.neutral += weighted.strong * 0.65;
    weighted.mistake += weighted.strong * 0.35;
    weighted.strong = 0;
  }
  if (buckets.neutral.length === 0) {
    weighted.strong += weighted.neutral * 0.6;
    weighted.mistake += weighted.neutral * 0.4;
    weighted.neutral = 0;
  }
  if (buckets.mistake.length === 0) {
    weighted.strong += weighted.mistake * 0.55;
    weighted.neutral += weighted.mistake * 0.45;
    weighted.mistake = 0;
  }
  return normalizeWeights(weighted);
}

function lineRunLength(board: BoardState, pos: Position, dr: number, dc: number): number {
  const cell = board.cells[pos.row]?.[pos.col];
  if (cell?.kind !== "TILE" || cell.tile == null) {
    return 0;
  }
  const color = cell.tile.color;

  let run = 1;
  let r = pos.row + dr;
  let c = pos.col + dc;
  while (r >= 0 && c >= 0 && r < board.size && c < board.size) {
    const next = board.cells[r][c];
    if (next.kind !== "TILE" || next.tile == null || next.tile.color !== color) {
      break;
    }
    run += 1;
    r += dr;
    c += dc;
  }
  r = pos.row - dr;
  c = pos.col - dc;
  while (r >= 0 && c >= 0 && r < board.size && c < board.size) {
    const prev = board.cells[r][c];
    if (prev.kind !== "TILE" || prev.tile == null || prev.tile.color !== color) {
      break;
    }
    run += 1;
    r -= dr;
    c -= dc;
  }
  return run;
}

function evaluateNearMatchPotential(board: BoardState, move: BotSwapCandidate): number {
  const preview = cloneBoard(board);
  swapCells(preview, move.from, move.to);
  const points = [move.from, move.to];
  let best = 0;
  for (const point of points) {
    const horizontal = lineRunLength(preview, point, 0, 1);
    const vertical = lineRunLength(preview, point, 1, 0);
    best = Math.max(best, horizontal, vertical);
  }
  return best;
}

function classifyCandidate(board: BoardState, move: BotSwapCandidate): BotMoveBucket {
  const activation = activationFromSwap(board, move.from, move.to);
  if (activation.triggered || createsMatchAfterSwap(board, move.from, move.to)) {
    return "strong";
  }
  const potential = evaluateNearMatchPotential(board, move);
  if (potential >= 2) {
    return "neutral";
  }
  return "mistake";
}

export function classifySwapCandidates(
  board: BoardState,
  candidates: BotSwapCandidate[]
): BotMoveBuckets {
  const buckets: BotMoveBuckets = {
    strong: [],
    neutral: [],
    mistake: []
  };

  for (const candidate of candidates) {
    const bucket = classifyCandidate(board, candidate);
    buckets[bucket].push(candidate);
  }

  return buckets;
}

function pickWeightedBucket(weights: BotMoveWeights, randomSource: RandomSource): BotMoveBucket {
  const roll = clamp(randomSource(), 0, 0.999999);
  if (roll < weights.strong) {
    return "strong";
  }
  if (roll < weights.strong + weights.neutral) {
    return "neutral";
  }
  return "mistake";
}

function pickFromBucket(
  bucket: BotSwapCandidate[],
  randomSource: RandomSource
): BotSwapCandidate | null {
  if (bucket.length === 0) {
    return null;
  }
  const index = Math.floor(clamp(randomSource(), 0, 0.999999) * bucket.length);
  return bucket[index] ?? bucket[bucket.length - 1] ?? null;
}

function canAffordTrap(kind: TrapKind, remoteCash: number): boolean {
  return remoteCash >= TRAP_CONFIGS[kind].cost;
}

function countUnlockableTiles(board: BoardState | null): number {
  if (board == null) {
    return 0;
  }
  let count = 0;
  for (let row = 0; row < board.size; row += 1) {
    for (let col = 0; col < board.size; col += 1) {
      const cell = board.cells[row]?.[col];
      if (cell?.kind === "TILE" && cell.tile != null && cell.tile.lockHp === 0) {
        count += 1;
      }
    }
  }
  return count;
}

export function chooseBotTrap(
  context: BotTrapDecisionContext,
  randomSource: RandomSource = Math.random
): TrapKind | null {
  const {
    localBoard,
    localFogActive,
    localCash,
    localDoomSec,
    localGroovePct,
    localGrooveActive,
    localGrooveArmed,
    remoteCash
  } = context;

  if (canAffordTrap("GROOVE_DRAIN", remoteCash) && (localGrooveActive || localGrooveArmed || localGroovePct >= 0.85)) {
    return "GROOVE_DRAIN";
  }
  if (canAffordTrap("DOOM_SURGE", remoteCash) && localDoomSec > 0 && localDoomSec <= 8) {
    return "DOOM_SURGE";
  }
  if (canAffordTrap("COIN_TAX", remoteCash) && localCash >= 250) {
    return "COIN_TAX";
  }
  if (canAffordTrap("LOCKDOWN_CHAINS", remoteCash) && countUnlockableTiles(localBoard) >= 4) {
    return "LOCKDOWN_CHAINS";
  }
  if (canAffordTrap("FOG_OVERLAY", remoteCash) && !localFogActive) {
    return "FOG_OVERLAY";
  }

  const nuisancePool: TrapKind[] = [];
  if (canAffordTrap("LOCKDOWN_CHAINS", remoteCash)) {
    nuisancePool.push("LOCKDOWN_CHAINS");
  }
  if (!localFogActive && canAffordTrap("FOG_OVERLAY", remoteCash)) {
    nuisancePool.push("FOG_OVERLAY");
  }
  if (canAffordTrap("COIN_TAX", remoteCash)) {
    nuisancePool.push("COIN_TAX");
  }
  if (nuisancePool.length === 0) {
    return null;
  }
  const index = Math.floor(clamp(randomSource(), 0, 0.999999) * nuisancePool.length);
  return nuisancePool[index] ?? nuisancePool[nuisancePool.length - 1] ?? null;
}

export function computeAntiSnowballTarget(
  skillProfile: BotSkillProfile,
  botLeadScoreDiff: number
): number {
  const start = Math.max(1, skillProfile.antiSnowball.leadStartScoreDiff);
  if (botLeadScoreDiff <= start) {
    return 0;
  }
  const overshoot = botLeadScoreDiff - start;
  return clamp(overshoot / start, 0, 1);
}

export function smoothAntiSnowballEase(
  previous: number,
  target: number,
  dt: number,
  easingStrength: number
): number {
  const rate = clamp(dt * clamp(easingStrength, 0.2, 1.2), 0, 1);
  return clamp(previous + ((target - previous) * rate), 0, 1);
}

export function computeAdaptiveBucketWeights(
  skillProfile: BotSkillProfile,
  runtime: BotDecisionRuntime
): BotMoveWeights {
  const fairness = skillProfile.fairness;
  const antiSnowball = skillProfile.antiSnowball;

  const baseStrong = clamp(
    0.24 + (fairness.pressure * 0.36) + (fairness.consistency * 0.22),
    0.2,
    0.74
  );
  const baseMistake = clamp(
    0.08 + (fairness.mistakeAllowance * 0.82) + ((1 - fairness.consistency) * 0.06),
    0.06,
    0.5
  );
  const baseNeutral = Math.max(0.08, 1 - baseStrong - baseMistake);

  let weights = normalizeWeights({
    strong: baseStrong,
    neutral: baseNeutral,
    mistake: baseMistake
  });

  const t = clamp(runtime.antiSnowballEase, 0, 1);
  if (t > 0) {
    weights = normalizeWeights({
      strong: weights.strong * (1 - (antiSnowball.pressureReductionMax * t)),
      neutral: weights.neutral * (1 + (antiSnowball.neutralWeightBoostMax * t)),
      mistake: weights.mistake * (1 + (antiSnowball.mistakeWeightBoostMax * t))
    });
  }

  return applyFairnessEnvelope(skillProfile, weights);
}

export function chooseBotMoveWithWeights(
  board: BoardState,
  candidates: BotSwapCandidate[],
  skillProfile: BotSkillProfile,
  runtime: BotDecisionRuntime,
  randomSource: RandomSource = Math.random,
  onDecision?: (diagnostics: BotDecisionDiagnostics) => void
): BotSwapCandidate | null {
  if (candidates.length === 0) {
    onDecision?.({
      selectedBucket: "fallback_any",
      antiSnowballTarget: computeAntiSnowballTarget(skillProfile, runtime.botLeadScoreDiff),
      antiSnowballEase: runtime.antiSnowballEase,
      leadScoreDiff: runtime.botLeadScoreDiff,
      leadStartScoreDiff: skillProfile.antiSnowball.leadStartScoreDiff,
      bucketsAvailable: { strong: 0, neutral: 0, mistake: 0 },
      baseWeights: { strong: 0, neutral: 0, mistake: 0 },
      effectiveWeights: { strong: 0, neutral: 0, mistake: 0 }
    });
    return null;
  }

  const buckets = classifySwapCandidates(board, candidates);
  const baseWeights = computeAdaptiveBucketWeights(skillProfile, runtime);
  const weights = bucketWeightsForAvailableMoves(baseWeights, buckets);
  const chosenBucket = pickWeightedBucket(weights, randomSource);
  const directPick = pickFromBucket(buckets[chosenBucket], randomSource);
  const antiSnowballTarget = computeAntiSnowballTarget(skillProfile, runtime.botLeadScoreDiff);
  const diagnosticsBase: Omit<BotDecisionDiagnostics, "selectedBucket"> = {
    antiSnowballTarget,
    antiSnowballEase: runtime.antiSnowballEase,
    leadScoreDiff: runtime.botLeadScoreDiff,
    leadStartScoreDiff: skillProfile.antiSnowball.leadStartScoreDiff,
    bucketsAvailable: {
      strong: buckets.strong.length,
      neutral: buckets.neutral.length,
      mistake: buckets.mistake.length
    },
    baseWeights: {
      strong: baseWeights.strong,
      neutral: baseWeights.neutral,
      mistake: baseWeights.mistake
    },
    effectiveWeights: {
      strong: weights.strong,
      neutral: weights.neutral,
      mistake: weights.mistake
    }
  };
  if (directPick != null) {
    onDecision?.({ ...diagnosticsBase, selectedBucket: chosenBucket });
    return directPick;
  }

  const fallbackPick = pickFromBucket(candidates, randomSource);
  onDecision?.({ ...diagnosticsBase, selectedBucket: "fallback_any" });
  return fallbackPick;
}

/**
 * AI bot input adapter: periodically picks weighted swaps
 * and feeds them into the mirror controller input stream.
 */
export class BotInputAdapter extends MirrorInputAdapter {
  private swapTimer = 0;
  private nextSwapDelay = 0;
  private grooveTimer = 0;
  private nextGrooveDelay = 0;
  private trapTimer = 0;
  private nextTrapDelay = 0;
  private antiSnowballEase = 0;
  private lastDecisionDiagnostics: BotDecisionDiagnostics | null = null;

  constructor(
    private readonly skillProfile: BotSkillProfile,
    private readonly randomSource: RandomSource = Math.random
  ) {
    super();
    this.nextSwapDelay = this.randomDelay();
    this.nextGrooveDelay = 8 + (this.randomSource() * 12);
    this.nextTrapDelay = this.randomTrapDelay();
  }

  private randomDelay(): number {
    return 0.8 + (this.randomSource() * 1.2); // 0.8-2.0 seconds
  }

  private randomTrapDelay(): number {
    const range = BOT_TRAP_DELAY_RANGES_SEC[this.skillProfile.segment];
    return range.min + (this.randomSource() * (range.max - range.min));
  }

  private buildSwapCandidates(board: BoardState): BotSwapCandidate[] {
    const candidates: BotSwapCandidate[] = [];

    for (let row = 0; row < board.size; row += 1) {
      for (let col = 0; col < board.size; col += 1) {
        const from = { row, col };

        const right = { row, col: col + 1 };
        if (col + 1 < board.size && canSwap(board, from, right)) {
          candidates.push({ from, to: right });
        }

        const down = { row: row + 1, col };
        if (row + 1 < board.size && canSwap(board, from, down)) {
          candidates.push({ from, to: down });
        }
      }
    }

    return candidates;
  }

  private updateAntiSnowballEase(dt: number, botLeadScoreDiff: number): void {
    const target = computeAntiSnowballTarget(this.skillProfile, botLeadScoreDiff);
    this.antiSnowballEase = smoothAntiSnowballEase(
      this.antiSnowballEase,
      target,
      dt,
      this.skillProfile.antiSnowball.easingStrength
    );
  }

  getLastDecisionDiagnostics(): BotDecisionDiagnostics | null {
    return this.lastDecisionDiagnostics;
  }

  /**
   * Call once per frame. Analyzes the board and queues moves.
   */
  updateBot(
    dt: number,
    board: BoardState | null,
    grooveFull: boolean,
    botLeadScoreDiff = 0,
    trapContext?: BotTrapDecisionContext
  ): TrapKind | null {
    if (board == null) return null;

    this.updateAntiSnowballEase(dt, botLeadScoreDiff);

    this.swapTimer += dt;
    if (this.swapTimer >= this.nextSwapDelay) {
      this.swapTimer = 0;
      this.nextSwapDelay = this.randomDelay();
      const candidates = this.buildSwapCandidates(board);
      const swap = chooseBotMoveWithWeights(
        board,
        candidates,
        this.skillProfile,
        {
          botLeadScoreDiff,
          antiSnowballEase: this.antiSnowballEase
        },
        this.randomSource,
        (diagnostics) => {
          this.lastDecisionDiagnostics = diagnostics;
        }
      );
      if (swap != null) {
        this.pushSwap(swap.from, swap.to);
      }
    }

    // Occasionally activate groove when meter is full
    if (grooveFull) {
      this.grooveTimer += dt;
      if (this.grooveTimer >= this.nextGrooveDelay) {
        this.grooveTimer = 0;
        this.nextGrooveDelay = 8 + (this.randomSource() * 12);
        this.pushAction("ACTIVATE_GROOVE");
      }
    }

    if (trapContext == null) {
      return null;
    }

    this.trapTimer += dt;
    if (this.trapTimer < this.nextTrapDelay) {
      return null;
    }

    this.trapTimer = 0;
    this.nextTrapDelay = this.randomTrapDelay();
    return chooseBotTrap(trapContext, this.randomSource);
  }
}
